import React, { useState, useRef, useCallback, useMemo } from "react";
import { X, Search } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { GlobalService } from "../../modules";

// Google Maps API TypeScript declarations
declare global {
  interface Window {
    google: any;
  }
}

interface RegionSettingsModalProps {
  region: any;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any, isEdit: boolean) => Promise<void>;
  formData: any;
  setFormData: (data: any) => void;
  languages: any[];
  currencies: any[];
  serviceAreas: any;
  createServiceAreaFromPlace: (place: any) => Promise<void>;
  isCreatingServiceArea: boolean;
  isGoogleMapsAvailable: boolean;
}

const RegionSettingsModal: React.FC<RegionSettingsModalProps> = ({
  region,
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData,
  languages,
  currencies,
  serviceAreas,
  createServiceAreaFromPlace,
  isCreatingServiceArea,
  isGoogleMapsAvailable
}) => {
  const [activeTab, setActiveTab] = useState("general");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serviceAreaSearchTerm, setServiceAreaSearchTerm] = useState("");
  const [autocompletePlace, setAutocompletePlace] = useState<any>(null);
  const [showAutocompleteSuggestions, setShowAutocompleteSuggestions] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  
  const serviceAreaInputRef = useRef<HTMLInputElement>(null);

  // Validation function
  const validateForm = useCallback(() => {
    const errors: {[key: string]: string} = {};
    
    // Required fields validation
    if (!formData.name?.trim()) {
      errors.name = "Region name is required";
    }
    
    if (!formData.code?.trim()) {
      errors.code = "Region code is required";
    }
    
    if (!formData.defaultLanguage?.id) {
      errors.defaultLanguage = "Default language is required";
    }
    
    if (!formData.defaultCurrency?.id) {
      errors.defaultCurrency = "Default currency is required";
    }
    
    if (!formData.quoteCurrency?.id) {
      errors.quoteCurrency = "Quote currency is required";
      // Show toast notification for quote currency validation
      GlobalService.showToast("error", "Quote Currency Required", "Please select a quote currency before saving the region settings.");
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  // Set default values for business hours
  const getDefaultBusinessHours = useCallback(() => {
    const now = new Date();
    
    // Default weekday hours: 9 AM to 6 PM
    const weekdayStart = new Date(now);
    weekdayStart.setHours(9, 0, 0, 0);
    
    const weekdayEnd = new Date(now);
    weekdayEnd.setHours(18, 0, 0, 0);
    
    // Default weekend hours: 10 AM to 4 PM
    const weekendStart = new Date(now);
    weekendStart.setHours(10, 0, 0, 0);
    
    const weekendEnd = new Date(now);
    weekendEnd.setHours(16, 0, 0, 0);
    
    return {
      businessWeekdayStartHour: weekdayStart.toISOString(),
      businessWeekdayEndHour: weekdayEnd.toISOString(),
      businessWeekendStartHour: weekendStart.toISOString(),
      businessWeekendEndHour: weekendEnd.toISOString()
    };
  }, []);

  // Set default values when formData changes
  React.useEffect(() => {
    if (formData && !region) { // Only set defaults for new regions
      const defaultHours = getDefaultBusinessHours();
      const hasEmptyHours = !formData.businessWeekdayStartHour || 
                           !formData.businessWeekdayEndHour || 
                           !formData.businessWeekendStartHour || 
                           !formData.businessWeekendEndHour;
      
      if (hasEmptyHours) {
        setFormData({
          ...formData,
          ...defaultHours
        });
      }
    }
  }, [formData, region, getDefaultBusinessHours, setFormData]);

  // Clear validation errors when modal is closed
  React.useEffect(() => {
    if (!isOpen) {
      setValidationErrors({});
    }
  }, [isOpen]);

  // Filtered service areas for search
  const filteredServiceAreas = useMemo(() => {
    if (!serviceAreaSearchTerm || serviceAreaSearchTerm.length < 2) return [];

    const query = serviceAreaSearchTerm.toLowerCase();
    return (serviceAreas?.data || []).filter((area: any) => {
      const nameMatches = area.name.toLowerCase().includes(query);
      const provinceMatches = area.province.toLowerCase().includes(query);
      const countryMatches = area.country?.name?.toLowerCase().includes(query);
      const cityMatches = area.cities?.some((city: string) =>
        city.toLowerCase().includes(query)
      );

      return nameMatches || provinceMatches || countryMatches || cityMatches;
    }).filter((area: any) =>
      !formData.serviceAreas.some((selectedArea: any) => selectedArea.id === area.id)
    );
  }, [serviceAreas?.data, serviceAreaSearchTerm, formData.serviceAreas]);

  // Function to clear autocomplete selection
  const clearAutocompleteSelection = useCallback(() => {
    setAutocompletePlace(null);
    setServiceAreaSearchTerm("");
    setShowAutocompleteSuggestions(false);
  }, []);

  // Initialize Google Maps Autocomplete for service areas
  const initServiceAreaAutocomplete = useCallback(() => {
    if (!window.google || !window.google.maps || !serviceAreaInputRef.current) {
      return;
    }

    const autocomplete = new window.google.maps.places.Autocomplete(serviceAreaInputRef.current, {
      types: ["geocode", "establishment"],
      componentRestrictions: { country: [] },
      fields: ["address_components", "formatted_address", "geometry", "name"]
    });

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        setAutocompletePlace(place);
        setServiceAreaSearchTerm(place.formatted_address || "");
        setShowAutocompleteSuggestions(true);
      }
    });
  }, []);

  // Initialize autocomplete when component mounts
  React.useEffect(() => {
    if (isOpen && isGoogleMapsAvailable) {
      setTimeout(() => {
        initServiceAreaAutocomplete();
      }, 100);
    }
  }, [isOpen, isGoogleMapsAvailable, initServiceAreaAutocomplete]);

  const handleSubmit = useCallback(async () => {
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit(formData, !!region);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, region, onSubmit, validateForm]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-7xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 md:p-6 border-b sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              {region ? `Edit ${region.name}` : "Add New Region"}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-4 md:p-6">
          {/* Tabs */}
          <div className="flex overflow-x-auto pb-2 mb-6 border-b">
            {["general", "pricing", "areas", "hours", "support"].map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`pb-4 px-2 whitespace-nowrap capitalize ${
                  activeTab === tab
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-500"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === "general" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Region Name</label>
                    <input
                      type="text"
                      className={`mt-1 block w-full px-3 py-2 border rounded-lg ${
                        validationErrors.name ? "border-red-500" : ""
                      }`}
                      placeholder="e.g. Canada"
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({...formData, name: e.target.value});
                        if (validationErrors.name) {
                          setValidationErrors(prev => {
                            const newErrors = {...prev};
                            delete newErrors.name;
                            return newErrors;
                          });
                        }
                      }}
                    />
                    {validationErrors.name && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Region Code</label>
                    <input
                      type="text"
                      className={`mt-1 block w-full px-3 py-2 border rounded-lg ${
                        validationErrors.code ? "border-red-500" : ""
                      }`}
                      placeholder="e.g. CA"
                      value={formData.code}
                      onChange={(e) => {
                        setFormData({...formData, code: e.target.value});
                        if (validationErrors.code) {
                          setValidationErrors(prev => {
                            const newErrors = {...prev};
                            delete newErrors.code;
                            return newErrors;
                          });
                        }
                      }}
                    />
                    {validationErrors.code && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.code}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Default Language</label>
                    <select 
                      className={`mt-1 block w-full px-3 py-2 border rounded-lg ${
                        validationErrors.defaultLanguage ? "border-red-500" : ""
                      }`}
                      value={formData.defaultLanguage.id}
                      onChange={(e) => {
                        setFormData({...formData, defaultLanguage: {id: e.target.value}});
                        if (validationErrors.defaultLanguage) {
                          setValidationErrors(prev => {
                            const newErrors = {...prev};
                            delete newErrors.defaultLanguage;
                            return newErrors;
                          });
                        }
                      }}
                    >
                      <option value="">Select Language</option>
                      {languages?.map((language: any) => (
                        <option key={language.id} value={language.id}>
                          {language.name}
                        </option>
                      ))}
                    </select>
                    {validationErrors.defaultLanguage && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.defaultLanguage}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Default Currency</label>
                    <select 
                      className={`mt-1 block w-full px-3 py-2 border rounded-lg ${
                        validationErrors.defaultCurrency ? "border-red-500" : ""
                      }`}
                      value={formData.defaultCurrency.id}
                      onChange={(e) => {
                        setFormData({...formData, defaultCurrency: {id: e.target.value}});
                        if (validationErrors.defaultCurrency) {
                          setValidationErrors(prev => {
                            const newErrors = {...prev};
                            delete newErrors.defaultCurrency;
                            return newErrors;
                          });
                        }
                      }}
                    >
                      <option value="">Select Currency</option>
                      {(currencies as any[])?.map((currency: any) => (
                        <option key={currency.value} value={currency.value}>
                          {currency.label}
                        </option>
                      ))}
                    </select>
                    {validationErrors.defaultCurrency && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.defaultCurrency}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Supported Languages
                  </label>
                  <div className="space-y-2">
                    {languages?.map((language: any) => {
                      const isSelected = formData.supportedLanguages.some((lang: any) => lang.id === language.id);
                      return (
                        <label key={language.id} className="flex items-center gap-2">
                          <input 
                            type="checkbox" 
                            className="rounded border-gray-300"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData, 
                                  supportedLanguages: [...formData.supportedLanguages, {id: language.id}]
                                });
                              } else {
                                setFormData({
                                  ...formData, 
                                  supportedLanguages: formData.supportedLanguages.filter((lang: any) => lang.id !== language.id)
                                });
                              }
                            }}
                          />
                          <span>{language.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "pricing" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-4">Quote Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Quote Amount</label>
                      <input
                        type="number"
                        className="mt-1 block w-full px-3 py-2 border rounded-lg"
                        placeholder="Enter amount"
                        value={formData.quoteAmount}
                        onChange={(e) => setFormData({...formData, quoteAmount: Number(e.target.value)})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Quote Currency</label>
                      <select 
                        className={`mt-1 block w-full px-3 py-2 border rounded-lg ${
                          validationErrors.quoteCurrency ? "border-red-500" : ""
                        }`}
                        value={formData.quoteCurrency.id}
                        onChange={(e) => {
                          setFormData({...formData, quoteCurrency: {id: e.target.value}});
                          if (validationErrors.quoteCurrency) {
                            setValidationErrors(prev => {
                              const newErrors = {...prev};
                              delete newErrors.quoteCurrency;
                              return newErrors;
                            });
                          }
                        }}
                      >
                        <option value="">Select Currency</option>
                        {(currencies as any[])?.map((currency: any) => (
                          <option key={currency.value} value={currency.value}>
                            {currency.label}
                          </option>
                        ))}
                      </select>
                      {validationErrors.quoteCurrency && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.quoteCurrency}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-4">Commission Rates</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Regular Service (%)</label>
                        <input
                          type="number"
                          className="mt-1 block w-full px-3 py-2 border rounded-lg"
                          placeholder="Enter percentage"
                          value={formData.commissionRegularService}
                          onChange={(e) => setFormData({...formData, commissionRegularService: Number(e.target.value)})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Emergency Service (Day) (%)</label>
                        <input
                          type="number"
                          className="mt-1 block w-full px-3 py-2 border rounded-lg"
                          placeholder="Enter percentage"
                          value={formData.commissionEmergencyServiceDay}
                          onChange={(e) => setFormData({...formData, commissionEmergencyServiceDay: Number(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Emergency Service (Night) (%)</label>
                        <input
                          type="number"
                          className="mt-1 block w-full px-3 py-2 border rounded-lg"
                          placeholder="Enter percentage"
                          value={formData.commissionEmergencyServiceNight}
                          onChange={(e) => setFormData({...formData, commissionEmergencyServiceNight: Number(e.target.value)})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Emergency Service (Weekend) (%)</label>
                        <input
                          type="number"
                          className="mt-1 block w-full px-3 py-2 border rounded-lg"
                          placeholder="Enter percentage"
                          value={formData.commissionEmergencyServiceWeekend}
                          onChange={(e) => setFormData({...formData, commissionEmergencyServiceWeekend: Number(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Emergency Service (Holiday) (%)</label>
                      <input
                        type="number"
                        className="mt-1 block w-full px-3 py-2 border rounded-lg"
                        placeholder="Enter percentage"
                        value={formData.commissionEmergencyServiceHoliday}
                        onChange={(e) => setFormData({...formData, commissionEmergencyServiceHoliday: Number(e.target.value)})}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "areas" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-4">Service Areas</h3>
                  
                  {/* Search Input */}
                  <div className="relative mb-4">
                    <input
                      ref={serviceAreaInputRef}
                      type="text"
                      placeholder="Search service areas or type a location..."
                      value={serviceAreaSearchTerm}
                      onChange={(e) => setServiceAreaSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg pr-10"
                      autoComplete="off"
                    />
                    {serviceAreaSearchTerm ? (
                      <button
                        type="button"
                        onClick={clearAutocompleteSelection}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    ) : (
                      <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    )}
                  </div>

                  {/* Google Maps API Status */}
                  {!isGoogleMapsAvailable && (
                    <div className="mb-4 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-xs text-yellow-800">
                        <strong>Note:</strong> Google Maps autocomplete is not available. 
                        You can still search for existing service areas or type a location manually.
                      </p>
                    </div>
                  )}

                  {/* Dropdown Options */}
                  {serviceAreaSearchTerm && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredServiceAreas.length > 0 ? (
                        filteredServiceAreas.map((serviceArea: any) => {
                          const searchTerm = serviceAreaSearchTerm.toLowerCase();
                          const matchingCities = Array.isArray(serviceArea.cities) ? 
                            serviceArea.cities.filter((city: string) => 
                              city.toLowerCase().includes(searchTerm)
                            ) : [];
                          const matchingPostalCodes = Array.isArray(serviceArea.postalCodes) ? 
                            serviceArea.postalCodes.filter((postalCode: string) => 
                              postalCode.toLowerCase().includes(searchTerm)
                            ) : [];
                          
                          return (
                            <div
                              key={serviceArea.id}
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  serviceAreas: [...prev.serviceAreas, { id: serviceArea.id }]
                                }));
                                setServiceAreaSearchTerm("");
                                setShowAutocompleteSuggestions(false);
                              }}
                              className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                            >
                              <div className="font-medium text-gray-900">
                                {serviceArea.name} - {serviceArea.province}
                              </div>
                              {(matchingCities.length > 0 || matchingPostalCodes.length > 0) && (
                                <div className="text-sm text-gray-600 mt-1">
                                  {matchingCities.length > 0 && (
                                    <div className="mb-1">
                                      <span className="text-gray-500">Cities: </span>
                                      {matchingCities.slice(0, 2).join(", ")}
                                      {matchingCities.length > 2 && ` +${matchingCities.length - 2} more`}
                                    </div>
                                  )}
                                  {matchingPostalCodes.length > 0 && (
                                    <div>
                                      <span className="text-gray-500">Postal Codes: </span>
                                      {matchingPostalCodes.slice(0, 2).join(", ")}
                                      {matchingPostalCodes.length > 2 && ` +${matchingPostalCodes.length - 2} more`}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : showAutocompleteSuggestions ? (
                        <div className="space-y-2 p-2">                                                            
                          {autocompletePlace && (
                            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-green-800">
                                    Selected Location:
                                  </p>
                                  <p className="text-sm text-green-700 break-words">
                                    {autocompletePlace.formatted_address}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={clearAutocompleteSelection}
                                    className="p-1 text-gray-400 hover:text-gray-600"
                                    title="Clear selection"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                  <Button
                                    onClick={() => createServiceAreaFromPlace(autocompletePlace)}
                                    disabled={isCreatingServiceArea}
                                    className="px-3 py-1 text-sm bg-green-600 hover:bg-green-700 text-white rounded"
                                  >
                                    {isCreatingServiceArea ? (
                                      <div className="flex items-center gap-1">
                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                        <span>Creating...</span>
                                      </div>
                                    ) : (
                                      "Create Service Area"
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}                                                        
                        </div>
                      ) : (
                        <div className="px-4 py-3 text-gray-500 text-center text-sm">
                          No service areas found
                        </div>
                      )}
                    </div>
                  )}

                  {/* Selected Service Areas */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-700">Selected Service Areas:</h4>
                    {formData.serviceAreas.length > 0 ? (
                      formData.serviceAreas.map((selectedArea: any) => {
                        const area = serviceAreas?.data?.find((sa: any) => sa.id === selectedArea.id);
                        return (
                          <div key={selectedArea.id} className="p-4 border rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="font-medium">{area?.name || "Unknown Area"}</div>
                                <div className="text-sm text-gray-600">
                                  {area?.province} • {area?.country?.name}
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    serviceAreas: prev.serviceAreas.filter((sa: any) => sa.id !== selectedArea.id)
                                  }));
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-4 border rounded-lg text-gray-500 text-center">
                        No service areas selected
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "hours" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-4">Business Hours</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Weekday Start</label>
                      <input
                        type="time"
                        className="mt-1 block w-full px-3 py-2 border rounded-lg"
                        value={formData.businessWeekdayStartHour ? new Date(formData.businessWeekdayStartHour).toTimeString().slice(0, 5) : ""}
                        onChange={(e) => {
                          const [hours, minutes] = e.target.value.split(":");
                          const date = new Date();
                          date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                          setFormData({...formData, businessWeekdayStartHour: date.toISOString()});
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Weekday End</label>
                      <input
                        type="time"
                        className="mt-1 block w-full px-3 py-2 border rounded-lg"
                        value={formData.businessWeekdayEndHour ? new Date(formData.businessWeekdayEndHour).toTimeString().slice(0, 5) : ""}
                        onChange={(e) => {
                          const [hours, minutes] = e.target.value.split(":");
                          const date = new Date();
                          date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                          setFormData({...formData, businessWeekdayEndHour: date.toISOString()});
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Weekend Start</label>
                      <input
                        type="time"
                        className="mt-1 block w-full px-3 py-2 border rounded-lg"
                        value={formData.businessWeekendStartHour ? new Date(formData.businessWeekendStartHour).toTimeString().slice(0, 5) : ""}
                        onChange={(e) => {
                          const [hours, minutes] = e.target.value.split(":");
                          const date = new Date();
                          date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                          setFormData({...formData, businessWeekendStartHour: date.toISOString()});
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Weekend End</label>
                      <input
                        type="time"
                        className="mt-1 block w-full px-3 py-2 border rounded-lg"
                        value={formData.businessWeekendEndHour ? new Date(formData.businessWeekendEndHour).toTimeString().slice(0, 5) : ""}
                        onChange={(e) => {
                          const [hours, minutes] = e.target.value.split(":");
                          const date = new Date();
                          date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                          setFormData({...formData, businessWeekendEndHour: date.toISOString()});
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "support" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Support Email</label>
                    <input
                      type="email"
                      className="mt-1 block w-full px-3 py-2 border rounded-lg"
                      placeholder="Enter support email"
                      value={formData.supportEmail}
                      onChange={(e) => setFormData({...formData, supportEmail: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Support Phone</label>
                    <input
                      type="tel"
                      className="mt-1 block w-full px-3 py-2 border rounded-lg"
                      placeholder="+1 (555) 123-4567"
                      value={formData.supportPhone}
                      onChange={(e) => setFormData({...formData, supportPhone: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 pt-6 border-t flex flex-col sm:flex-row justify-end gap-3">
            <Button 
              type="button"
              variant="outline" 
              onClick={onClose} 
              className="w-full sm:w-auto"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              type="button"
              className="w-full sm:w-auto"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegionSettingsModal;
